'use client';

import {
  useEffectWithController,
  useLoadableState
} from '@carvana/merch-react-hooks';
import { useContext } from 'react';
import { httpWithAuth } from '~/clients';
import { VEHICLE_PRICING } from '~/constants/endpoints';
import CommonStaticContext from '~/context/common/StaticContext';
import { useUserLocation } from '~/context/common/UserContext';
import type { VehiclePricingResponse } from '~/services/common/contracts/PricingResponse';
import { BrowserCookies } from '~/utilities';

type OwnArgs = {
  routeId: string | undefined;
  skip: boolean;
};

export default function useNewCarPricing(
  vehicleId: number,
  { routeId, skip }: OwnArgs
) {
  const { clientId, eventCorrelationId } = useContext(CommonStaticContext);
  const { zip } = useUserLocation();
  const { data, isLoaded, isLoading, setData, setError, setLoading } =
    useLoadableState<Partial<VehiclePricingResponse>>({});

  useEffectWithController(
    controller => {
      if (skip) return;

      setLoading();

      httpWithAuth
        .post<VehiclePricingResponse>(
          VEHICLE_PRICING,
          {
            analyticsData: {
              clientId,
              correlationId: eventCorrelationId,
              utmParams: {}
            },
            applyTradeIn: false,
            applyVerboseLogging: false,
            browserCookieId: BrowserCookies.get('BrowserCookieId') ?? '',
            preferredAcquisitionName: '',
            routeId,
            vehicleId,
            zip5: zip
          },
          { signal: controller.signal }
        )
        .then(({ data: response }) => {
          setData(response);
        })
        .catch(error => {
          setError(error);
        });
    },
    [
      clientId,
      eventCorrelationId,
      routeId,
      setData,
      setError,
      setLoading,
      skip,
      vehicleId,
      zip
    ]
  );

  return { data, isLoaded, isLoading };
}
